import React from 'react';
import { shallowEqual } from 'react-redux';
import Templater, { IDownloadAndParseTemplateByName } from './Templater';

/**
 * Permite la utilización de un template de Apia sin la necesidad de
 * preocuparse por los detalles de implementación. Dado un nombre de template
 * de Apia la utilización de este hook es extremadamente sencilla.
 *
 * @important
 * The options object should be memoized in order to avoid unnecessary
 * template parsing.
 *
 * @example
 * const template = useTemplate('modals.documentInfo');
 * return template;
 *
 *
 * @see Templater.downloadAndParseTemplateByName method
 * @see templates.md en el repositorio de Apia.
 *
 * @param name Nombre del template de Apia.
 * @returns
 */
export default function useTemplate(
  name: string,
  options?: Partial<IDownloadAndParseTemplateByName> & {
    /** If this parameter is set to true, name will be treated as an url */
    isUrl?: boolean;
    /**
     * Warning: If this parameter is set to true, you must be sure to
     * memo the passed props. Otherwise, it will trigger a new reparse
     * of the template each time the Component renders.
     *
     * With this parameter to true, it is possible to reparse the template
     * each time the props object changes, making
     */
    reparseOnPropsChange?: boolean;
  },
) {
  const [template, setTemplate] = React.useState<Awaited<
    ReturnType<typeof Templater['downloadAndParseTemplateByName']>
  > | null>(null);
  const prevProps = React.useRef(options?.props);
  React.useEffect(() => {
    if (
      (options?.reparseOnPropsChange &&
        !shallowEqual(options?.props, prevProps.current)) ||
      !template
    ) {
      (options?.isUrl
        ? Templater.getTemplateFromUrl(name, options)
        : Templater.downloadAndParseTemplateByName(name, options)
      )
        .then(setTemplate)
        .catch(console.error);
      prevProps.current = options?.props;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.props]);
  return template;
}
